import { css } from "@emotion/css";
import { Divider, Grid, MenuItem } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { ClientUser } from "shared/models/interfaces/client-user.interface";
import { mixed, number, object, string } from "yup";
import Button from "../../Button";
import ProfilePicture from "../../profile-picture";
import SelectField from "../../select-field";
import TextField from "../../text-field";
import UploadIcon from "@mui/icons-material/Upload";
import { ChangeEvent, useEffect, useState } from "react";
import { CedulaMask, PhoneMask } from "../../mask-components";
import {
  allowedFormatTypes,
  byteValueNumberFormatter,
  FileValidationStateInvalid,
  imgFromFile,
  MAX_FILE_SIZE,
} from "@helpers/upload-file.helper";
import useSnackBar from "custom-hooks/useSnackBar.hook";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCountries, userUpdateProfile } from "api/clients/clients.request";
import { useAppDispatch } from "custom-hooks/redux.hooks";
import { setUser } from "store/feature/auth/auth.slice";
import Link from "next/link";
import { Country } from "@interfaces/countries.interface";
import { CPagesPath } from "shared/models/const/pages-path";
import { useLocale, useTranslations } from "next-intl";
import styled from "@emotion/styled";
import { useRegionName } from "@helpers/region-name.helper";
import removeNullValues from "@helpers/remove-null-values.helper";

interface IEditProfileForm {
  user: ClientUser;
  onSubmitSuccess?: (data: any) => void;
}

interface ClientUserForm extends Omit<ClientUser, "profile_picture"> {
  country_id: number | null;
  profile_picture: string | File | null;
  identification_type?: string | null;
}

const socialNetworks = [
  "whatsapp",
  "facebook",
  "instagram",
  "twitter",
  "tiktok",
];

const typeIdentificationList = [
  { name: "Cédula", value: "Cedula" },
  { name: "Pasaporte", value: "Pasaporte" },
];
const userUpdateProfile$ = ({ body, asFormData }: any) =>
  userUpdateProfile(body, asFormData);

export default function EditProfileForm({
  user,
  onSubmitSuccess,
}: IEditProfileForm) {
  const t = useTranslations("ProfileEdit.form");
  const tErrors = useTranslations("error");
  const { showMessage } = useSnackBar();
  const dispatch = useAppDispatch();
  const [countriesList, setCountriesList] = useState<Country[]>([]);
  const [profilePicture, setProfilePicture] = useState<string | null>(
    user.profile_picture
  );
  const queryClient = useQueryClient();
  const locale = useLocale();
  const regionHelper = useRegionName(locale);

  const urlRegex = (page: string) =>
    new RegExp(`^(https:\/\/)(www\.)?${page}\.com`);

  const validationSchema = object({
    full_name: string()
      .min(5, tErrors("field-min", { min: 5 }))
      .required(tErrors("required"))
      .trim(),
    profile_picture: mixed().nullable(),
    phone: string()
      .min(10, tErrors("field-min", { min: 10 }))
      .nullable(),
    cellphone: string()
      .min(10, tErrors("field-min", { min: 10 }))
      .nullable(),
    identification_type: string().nullable(),
    identification: string()
      //@ts-ignore
      .when("identification_type", (identification_type: any) => {
        if (identification_type === typeIdentificationList[0].value) {
          return string()
            .matches(/^[0-9]{11}$/, "Cédula no valida, debe tener 11 dígitos")
            .nullable();
        } else if (identification_type === typeIdentificationList[1].value) {
          return string()
            .matches(/^[A-Za-z0-9]+$/, "Solo letras y números")
            .nullable();
        }
      })
      .nullable(),
    address: string().nullable(),
    country_id: number().nullable(),
    sex: string().nullable(),
    social_networks: object({
      whatsapp: string()
        .min(10, tErrors("field-min", { min: 10 }))
        .nullable(),
      facebook: string()
        .matches(urlRegex("facebook"), tErrors("socialUrl"))
        .nullable(),
      instagram: string()
        .matches(urlRegex("instagram"), tErrors("socialUrl"))
        .nullable(),
      twitter: string()
        .matches(urlRegex("twitter"), tErrors("socialUrl"))
        .nullable(),
      tiktok: string()
        .matches(urlRegex("tiktok"), tErrors("socialUrl"))
        .nullable(),
    }).nullable(),
  });
  const { data: countries } = useQuery(["list/countries"], getCountries, {
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  useEffect(() => {
    if (countries) setCountriesList(countries);
  }, [countries]);

  const initialValues: ClientUserForm = {
    ...user,
    country_id: user.country_id ?? null,
  };


  const { mutate, isLoading } = useMutation(userUpdateProfile$, {
    onSuccess: (user) => {
      dispatch(setUser(user));
      if (onSubmitSuccess) {
        onSubmitSuccess(user);
        queryClient.invalidateQueries(["user/profile"]);
      }
    },
    onError: console.log,
  });

  const handleProfilePictureUpload = (
    formik: FormikProps<ClientUserForm>,
    { currentTarget }: ChangeEvent<HTMLInputElement>
  ) => {
    const file = currentTarget.files?.item(0);

    imgFromFile(file as File)
      .then((src) => {
        formik.setFieldValue("profile_picture", file);
        setProfilePicture(src as string);
      })
      .catch((reason: FileValidationStateInvalid) => {
        let message;
        if (reason === "exceeded-size") {
          message = tErrors(reason, {
            max: byteValueNumberFormatter.format(MAX_FILE_SIZE),
          });
        } else if (reason === "wrong-format") {
          message = tErrors(reason, { formats: allowedFormatTypes.join(", ") });
        }
        showMessage(message, {
          variant: "error",
        });
      });
  };

  const handleRemoveProfilePicture = (formik: FormikProps<ClientUserForm>) => {
    formik.setFieldValue("profile_picture", null);
    setProfilePicture(null);
  };

  function handleSubmit(data: Partial<ClientUserForm>) {

    let body = { ...removeNullValues(data,{
      removeEmptyStrings: true,
      removeEmptyArray: false,
    })};
    let asFormData = false;

    // delete body.email;

    if (body.profile_picture instanceof File) asFormData = true;

    if (body.profile_picture === user.profile_picture)
      delete body.profile_picture;

    delete body.type;

    mutate({ body, asFormData });
  }

  const intlPhoneNumber = (numStr: any) => {
    if (numStr === "") return "";

    if (/^(809|829|849).*/.test(numStr) && numStr.length <= 10) {
      return numStr.replace(
        /(\d{3})(\d{3})?(\d{4})?/,
        numStr.length <= 6 ? "($1) $2" : "($1) $2-$3"
      );
    }

    return numStr;
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false} //nested fields slow down performance!
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form className={gridContainer}>
          <Grid container>
            <Grid container item xs={12} className={profilePictureGrid}>
              <Grid item sx={{ mb: "10px", mr: "10px" }}>
                <ProfilePicture src={profilePicture} size={225} />
                <input
                  type="file"
                  id="profile_picture-input"
                  name="profile_picture"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleProfilePictureUpload(formik, e)}
                />
              </Grid>
              <Grid item className="action-buttons">
                <Button
                  className={labelButton}
                  thin_button
                  bg="--color-brand-blue-main"
                >
                  <label
                    htmlFor="profile_picture-input"
                    className="upload-button-lbl"
                  >
                    <UploadIcon />
                    {t("buttons.upload-picture")}
                  </label>
                </Button>
                <Button
                  className={noWrap}
                  thin_button
                  onClick={() => handleRemoveProfilePicture(formik)}
                >
                  {t("buttons.delete-picture")}
                </Button>
              </Grid>
            </Grid>
            <Grid item xs={12} mb={3}>
              <Divider sx={{ my: 3 }} />
              <SectionSubTitle>{t("sub-header.personal-data")}</SectionSubTitle>
            </Grid>
            <Grid container item xs={12} rowSpacing={3} columnSpacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="full_name"
                  fullWidth
                  label={t("field.full_name") + "*"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label={t("field.phone")}
                  inputProps={{
                    format: intlPhoneNumber,
                    maxLength: 15,
                  }}
                  inputComponent={PhoneMask}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="cellphone"
                  label={t("field.cellphone")}
                  inputProps={{
                    format: intlPhoneNumber,
                    maxLength: 15,
                  }}
                  inputComponent={PhoneMask}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="address" label={t("field.address")} />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* TODO: User virtualized list */}
                <SelectField
                  name="country_id"
                  fullWidth
                  label={t("field.country")}
                  items={countriesList}
                  MenuProps={{ sx: { maxHeight: 400 } }}
                >
                  {countriesList.map(({ code, id }, i) => (
                    <MenuItem key={i} value={id}>
                      {regionHelper.of(code)}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* TODO: User virtualized list */}
                <SelectField
                  name="identification_type"
                  fullWidth
                  label={t("field.identification_type")}
                  items={countriesList}
                  MenuProps={{ sx: { maxHeight: 400 } }}
                >
                  {typeIdentificationList.map(({ name, value }, i) => (
                    <MenuItem key={i} value={value}>
                      {name}
                    </MenuItem>
                  ))}
                </SelectField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="identification"
                  label={t("field.identification")}
                  inputComponent={
                    formik?.values?.identification_type ==
                    typeIdentificationList[0].value
                      ? CedulaMask
                      : "input"
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <SelectField
                  name="sex"
                  fullWidth
                  label={t("field.sexo")}
                  items={[
                    {
                      value: "female",
                      label: locale === "es" ? "Femenino" : "Female",
                    },
                    {
                      value: "male",
                      label: locale === "es" ? "Masculino" : "Male",
                    },
                  ]}
                />
              </Grid>
            </Grid>
            <Grid container item xs={12} rowSpacing={3}>
              <Grid item xs={12}>
                <Divider flexItem sx={{ my: 3 }} />
                <SectionSubTitle>
                  {t("sub-header.social-networks")}
                </SectionSubTitle>
              </Grid>
              {socialNetworks.map((name, i) => {
                let otherProps =
                  name === "whatsapp"
                    ? {
                        inputProps: {
                          format: intlPhoneNumber,
                          maxLength: 15,
                        },
                        inputComponent: PhoneMask,
                      }
                    : null;

                return (
                  <Grid item xs={12} key={i}>
                    <TextField
                      name={"social_networks." + name}
                      fullWidth
                      label={t(("field.social_networks." + name) as any)}
                      {...otherProps}
                    />
                  </Grid>
                );
              })}
            </Grid>
            <Grid item xs={12} m="auto" mt={3} className={formButtonsContainer}>
              <Button
                fullWidth
                thin_button
                className={noWrap}
                type="submit"
                disabled={isLoading}
                loadingMessage={isLoading ? t("buttons.submitLoading") : null}
              >
                {t("buttons.submit")}
              </Button>
              <Link href={CPagesPath.es.profile}>
                <Button
                  fullWidth
                  className={noWrap}
                  thin_button
                  bg="--color-brand-grey-secondary"
                >
                  {t("buttons.undo")}
                </Button>
              </Link>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}

const gridContainer = css`
  .MuiInputBase-root {
    background-color: white;
  }

  .upload-button-lbl {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 6px;
    white-space: nowrap;
  }
`;

const labelButton = css`
  padding: 0;
  overflow: clip;
`;

const noWrap = css`
  white-space: nowrap;
`;

const SectionSubTitle = styled.h4`
  padding-left: 10px;
  color: var(--color-brand-grey-quaternary);
`;

const profilePictureGrid = css`
  display: flex;
  justify-content: center;
  max-width: max-content;

  .action-buttons {
    display: grid;
    place-content: center;
    gap: 10px;
    grid-template-columns: 1fr;
  }
`;

const formButtonsContainer = css`
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(auto-fit, 200px);
  grid-gap: 10px;
  direction: rtl;
`;
